import { useI18n } from 'vue-i18n'
import allowed from '@/core/constants/allowed-roles.constant'
import { menuItems } from '@/core/constants/new-menu-items.constant'
import { injectRiskOverviewMenuItemIfAllowed } from '@/core/utils/inject-risk-overview-menu.util'
import { useAuthStore } from '@/modules/auth/stores/auth.store'

function groupDashboardsByPillar(dashboards) {
  return dashboards.reduce((accumulator, item) => {
    const key = item.pillar

    if (!accumulator[key]) {
      accumulator[key] = []
    }

    accumulator[key].push(item)

    return accumulator
  }, {})
}

function mergeDashboardsIntoModules(localMenuItems, groupedDashboards, allDashboardsByPillar, t, locale) {
  localMenuItems.modules.forEach((module) => {
    if (!module.pillar)
      return

    // Verifica se existe algum dashboard para esse pilar (não apenas favoritos)
    const hasAnyDashboard = allDashboardsByPillar[module.pillar]?.length > 0
    if (!hasAnyDashboard)
      return

    const pillarDashboards = groupedDashboards[module.pillar] || []

    // Se não tem dashboards favoritados para esse pilar
    if (pillarDashboards.length === 0) {
      module.children.push({
        name: 'core.menu.modules.dashboards',
        icon: 'pi pi-chart-bar',
        route: `/dashboards/pillar/${module.pillar}`,
        type: 'core',
        allowedFlag: 'isEnabledDashboards',
      })
      return
    }

    // Se tem dashboards favoritados
    const dashboardMenuItem = {
      name: 'core.menu.modules.dashboards',
      icon: 'pi pi-chart-bar',
      type: 'core',
      allowedFlag: 'isEnabledDashboards',
      children: [
        // Primeiro item: All Dashboards
        {
          name: 'core.menu.modules.all-dashboards',
          route: `/dashboards/pillar/${module.pillar}`,
          type: 'sub',
          allowedFlag: 'isEnabledDashboards',
        },
      ],
    }

    // Adiciona os dashboards favoritados
    pillarDashboards.forEach((dashboard, index) => {
      dashboardMenuItem.children.push({
        name: dashboard.nameLang?.[locale] || dashboard.nameLang?.en || dashboard.name || `Unnamed Dashboard ${index + 1}`,
        route: `/dashboards/${dashboard.id}`,
        type: 'sub',
        allowedFlag: 'isEnabledDashboards',
      })
    })

    module.children.push(dashboardMenuItem)
  })
}

function buildActiveShortcutItems(shortcuts, locale) {
  return shortcuts
    .filter(s => s.active)
    .map(s => ({
      name: s.titleLang?.[locale] || s.titleLang?.en || s.title || '',
      isRawName: true,
      icon: s.icon ? s.icon.replace(/^(?:pi\s+)?pi-/, '') : 'link',
      route: s.url,
      type: 'sub',
      openInNewTab: s.openInNewTab !== false,
    }))
}

function injectShortcutsIntoMenu(localMenuItems, shortcuts, displayMode, locale) {
  const items = buildActiveShortcutItems(shortcuts, locale)
  if (items.length === 0)
    return

  if (displayMode === 'individual') {
    localMenuItems.modules.unshift(...items.map(item => ({ ...item, type: 'core' })))
  }
  else {
    localMenuItems.modules.unshift({
      name: 'core.menu.modules.shortcuts',
      icon: 'bolt',
      type: 'core',
      children: items,
    })
  }
}

export function useDynamicMenu() {
  const authStore = useAuthStore()
  const { t, locale } = useI18n()

  const addDynamicDashboardMenuItems = () => {
    const localMenuItems = JSON.parse(JSON.stringify(menuItems))
    const companyData = authStore.companyData
    injectRiskOverviewMenuItemIfAllowed(localMenuItems, companyData?.id)
    const isDashboardsEnabled = companyData?.isEnabledDashboards
    const allDashboards = companyData?.enabledDashboards || []
    const favoriteDashboards = allDashboards.filter(dashboard => dashboard.isFavorite)
    const groupedFavorites = groupDashboardsByPillar(favoriteDashboards)
    const allDashboardsByPillar = groupDashboardsByPillar(allDashboards)

    const hasPermission = authStore.userRoles?.some(role => allowed.dashboard.roles.includes(role))

    if (isDashboardsEnabled && hasPermission) {
      mergeDashboardsIntoModules(localMenuItems, groupedFavorites, allDashboardsByPillar, t, locale.value)
    }

    const shortcuts = companyData?.shortcuts || []
    const shortcutDisplayMode = companyData?.shortcutDisplayMode || 'grouped'
    if (shortcuts.length > 0) {
      injectShortcutsIntoMenu(localMenuItems, shortcuts, shortcutDisplayMode, locale.value)
    }

    return localMenuItems
  }

  const loadDynamicMenuItems = () => {
    return addDynamicDashboardMenuItems()
  }

  return {
    loadDynamicMenuItems,
  }
}
